import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, ChevronDown, ExternalLink, FileText, Paperclip, Plus, Trash2, Users, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import { CLASSE_DE_CAMPO } from '@/features/admin/componentes';
import { listarOperadores, listarPoliticasSla, chavesDeAdministracao } from '@/features/admin/admin.api';
import { listarServicos, chavesCatalogo } from '@/features/catalog/catalog.api';
import {
  adicionarAnexo,
  adicionarParte,
  adicionarServicoDeTermo,
  alterarContrato,
  ativarTermo,
  chavesDeContratos,
  criarRegraDeEscopo,
  criarTermo,
  excluirRegraDeEscopo,
  excluirRascunhoDeTermo,
  listarEscopoDoTermo,
  obterContrato,
  obterHistoricoDeUso,
  obterPainel,
  removerAnexo,
  removerParte,
  removerServicoDeTermo,
  renovarTermo,
  rescindirContrato,
  retomarContrato,
  suspenderContrato,
  type ContractAttachmentKind,
  type ContractDetail,
  type ContractPanel,
  type ContractStakeholderRow,
  type ContractTermRow,
  type ContractType,
  type NovoAnexo,
  type NovaParte,
  type RegraDeEscopo,
  type StakeholderRole,
  type StakeholderSide,
} from '@/features/contratos/contratos.api';
import { cn } from '@/lib/cn';
import { Aviso, Etiqueta, Indicador, useEscritaDeContrato } from '@/features/contratos/componentes';
import {
  CAMINHO_LISTA,
  COBERTURAS,
  TIPOS_DE_CONTRATO,
  direcaoDeContrato,
  situacaoDeContrato,
  situacaoDeTermo,
  tipoDeContrato,
} from '@/features/contratos/navegacao';
import { Modal } from '@/features/tickets/Modal';
import { formatarData, formatarDataHora, formatarMinutos } from '@/lib/format';

/**
 * A ficha do contrato.
 *
 * ## A linha do tempo dos termos é o coração da tela
 *
 * O contrato é a identidade comercial; o **termo** é o que vence e o que
 * carrega as condições. É por isso que a ficha mostra todos os termos, e não
 * só o vigente: "qual era o prazo deste chamado em março?" se responde olhando
 * o termo de março, e ele continua ali depois de duas renovações — substituído,
 * nunca sobrescrito.
 *
 * ## O que a tela **não** deixa fazer, e diz por quê
 *
 * Termo ativo não tem botão de editar. Não é permissão faltando: renovar e
 * aditar criam um termo novo, de propósito, e é isso que mantém o histórico
 * respondendo a verdade. A tela explica no lugar de esconder, porque um botão
 * ausente sem explicação vira um chamado para o suporte interno.
 */
export function ContratoDetalhePage({ contratoId }: { contratoId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeContratos.ficha(contratoId),
    queryFn: () => obterContrato(contratoId),
  });
  const painel = useQuery({
    queryKey: chavesDeContratos.painel(contratoId),
    queryFn: () => obterPainel(contratoId),
  });

  const [criandoTermo, setCriandoTermo] = useState(false);
  const [editando, setEditando] = useState(false);
  const [decidindo, setDecidindo] = useState<'suspend' | 'terminate' | null>(null);
  const [historicoAberto, setHistoricoAberto] = useState(false);

  if (consulta.isError) {
    return (
      <div className="mx-auto max-w-5xl py-8">
        <ErrorState
          error={consulta.error}
          title="Não foi possível carregar o contrato"
          onRetry={() => void consulta.refetch()}
        />
      </div>
    );
  }

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando o contrato" className="mx-auto max-w-5xl space-y-4 py-8">
        <SkeletonText lines={8} />
      </SkeletonRegion>
    );
  }

  const contrato = consulta.data;
  const situacao = situacaoDeContrato(contrato.status);
  const termoVigente = contrato.terms.find((t) => t.emVigor) ?? null;

  return (
    <>
      <div className="mx-auto max-w-5xl space-y-6 animate-fade-in">
        <Link
          to={CAMINHO_LISTA}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Contratos
        </Link>

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{contrato.name}</h1>
              <Etiqueta rotulo={situacao.rotulo} tom={situacao.tom} titulo={situacao.origem} />
              {(() => { const dir = direcaoDeContrato(contrato.direction); return <Etiqueta rotulo={dir.rotulo} tom={dir.tom} />; })()}
            </div>
            <p className="font-mono text-sm text-muted-foreground">
              {contrato.number}
              {contrato.externalNumber ? ` · ${contrato.externalNumber}` : ''} · {contrato.accountName}
            </p>
            <p className="text-xs text-muted-foreground">
              {tipoDeContrato(contrato.type).rotulo}
              {tipoDeContrato(contrato.type).nota ? ` — ${tipoDeContrato(contrato.type).nota}` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            {contrato.terminatedAt === null ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCriandoTermo(true)}
                  icon={<Plus className="h-4 w-4" aria-hidden />}
                >
                  Novo termo
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditando(true)}>
                  Editar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDecidindo('suspend')}>
                  {contrato.suspendedAt ? 'Retomar' : 'Suspender'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDecidindo('terminate')}>
                  Rescindir
                </Button>
              </>
            ) : null}
          </div>
        </header>

        {/**
         * A situação é derivada — a tela diz isso em voz alta.
         *
         * É a diferença que o dono do produto vai querer conferir: no sistema
         * anterior, metade dos contratos "Vigentes" já tinha vencido, porque
         * alguém digitava a palavra e ninguém voltava para corrigi-la.
         */}
        <Aviso tom="info" titulo="A situação deste contrato não é digitada por ninguém">
          <p>
            {situacao.origem} A varredura diária do sistema encerra o termo cuja data passou e
            avisa o gerente de conta {contrato.renewalWarnDays} dia(s) antes do vencimento.
          </p>
        </Aviso>

        {contrato.suspendedAt ? (
          <Aviso tom="alerta" titulo={`Suspenso em ${formatarData(contrato.suspendedAt)}`}>
            <p>{contrato.suspensionReason}</p>
            <p className="mt-1">
              Chamado novo passa a nascer fora de vigência, com prazo não contratual. O que já
              estava aberto continua exatamente como está.
            </p>
          </Aviso>
        ) : null}

        {contrato.terminatedAt ? (
          <Aviso tom="perigo" titulo={`Rescindido em ${formatarData(contrato.terminatedAt)}`}>
            <p>{contrato.terminationReason}</p>
            <p className="mt-1">
              Nada foi apagado: os termos, os chamados e o histórico de prazos continuam
              consultáveis.
            </p>
          </Aviso>
        ) : null}

        <PainelDoContrato painel={painel.data} carregando={painel.isPending} contratoId={contratoId} />

        {contrato.terms.length > 1 && <TimelineDeTermos terms={contrato.terms} />}

        <Card>
          <CardHeader
            title="Termos"
            description="O termo é o que vence e o que carrega as condições. Renovar e aditar criam um termo novo — o anterior fica como estava, e é ele que responde qual era o prazo de um chamado antigo."
          />
          <CardBody className="space-y-3">
            {contrato.terms.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-7 w-7" aria-hidden />}
                title="Nenhum termo ainda"
                description="O contrato existe como minuta e não governa nada. Crie o primeiro termo com a vigência e os serviços cobertos."
              />
            ) : (
              contrato.terms.map((termo) => (
                <LinhaDeTermo key={termo.id} termo={termo} contrato={contrato} />
              ))
            )}
          </CardBody>
        </Card>

        {contrato.amendments.length > 0 ? (
          <Card>
            <CardHeader
              title="Aditivos"
              description="O aditivo é o documento; o efeito dele é sempre um termo novo. O valor e o prazo de antes continuam consultáveis no termo anterior."
            />
            <CardBody className="space-y-2">
              {contrato.amendments.map((aditivo) => (
                <div key={aditivo.id} className="rounded-lg border border-border px-4 py-3">
                  <p className="text-sm font-medium">
                    Aditivo {aditivo.number} · {formatarData(aditivo.signedAt)}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">{aditivo.summary}</p>
                </div>
              ))}
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardHeader title="Assinatura e documento" />
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Dado rotulo="Assinado por" valor={contrato.signedBy ?? '—'} />
            <Dado
              rotulo="Data da assinatura"
              valor={contrato.signedOnDate ? formatarData(contrato.signedOnDate) : '—'}
              nota="A assinatura pertence ao contrato, não ao termo: ela costuma anteceder o termo em vigor em anos."
            />
            {contrato.demandingArea && (
              <Dado rotulo="Área demandante" valor={contrato.demandingArea} />
            )}
            <Dado rotulo="Moeda" valor={contrato.currency} />
            <Dado
              rotulo="Aviso de vencimento"
              valor={`${contrato.renewalWarnDays} dia(s) antes`}
              nota="Conferido todo dia pelo sistema. Não é uma data digitada — data de lembrete envelhece e aponta para depois do vencimento."
            />
            <Dado rotulo="Criado em" valor={formatarDataHora(contrato.createdAt)} />
            <Dado rotulo="Atualizado em" valor={formatarDataHora(contrato.updatedAt)} />
          </CardBody>
        </Card>

        <SecaoDePartes contrato={contrato} />
        <SecaoDeDocumentos contrato={contrato} />

        <section className="space-y-3">
          <button
            onClick={() => setHistoricoAberto((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground w-full"
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', historicoAberto && 'rotate-180')}
            />
            Histórico de alterações
          </button>
          {historicoAberto && (
            <div className="mt-4">
              <HistoricoView contrato={contrato} />
            </div>
          )}
        </section>
      </div>

      <DialogoDeTermo
        contratoId={contrato.id}
        aberto={criandoTermo}
        aoFechar={() => setCriandoTermo(false)}
      />
      <DialogoDeEdicaoDeContrato
        contrato={contrato}
        aberto={editando}
        aoFechar={() => setEditando(false)}
      />
      <DialogoDeDecisao
        contrato={contrato}
        acao={decidindo}
        aoFechar={() => setDecidindo(null)}
        termoVigente={termoVigente}
      />
    </>
  );
}

function Dado({ rotulo, valor, nota }: { rotulo: string; valor: string; nota?: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{rotulo}</p>
      <p className="mt-0.5 text-sm">{valor}</p>
      {nota ? <p className="mt-1 text-xs text-muted-foreground">{nota}</p> : null}
    </div>
  );
}

/**
 * O painel: horas consumidas, chamados no período e aderência ao prazo.
 *
 * Os números são do **termo em vigor**, e o rótulo diz isso: "consumiu 340
 * horas" sem dizer de qual período é um número que não confere com nada, e é
 * exatamente o que o cliente contesta na reunião mensal.
 */
function PainelDoContrato({
  painel,
  carregando,
  contratoId,
}: {
  painel: ContractPanel | undefined;
  carregando: boolean;
  contratoId: string;
}) {
  const histQuery = useQuery({
    queryKey: ['contratos', 'historico-uso', contratoId],
    queryFn: () => obterHistoricoDeUso(contratoId),
  });

  if (carregando) {
    return (
      <SkeletonRegion label="Carregando o painel" className="space-y-3">
        <SkeletonText lines={3} />
      </SkeletonRegion>
    );
  }
  if (!painel) return null;

  if (painel.termId === null) {
    return (
      <Aviso tom="alerta" titulo="Sem termo em vigor — não há período para medir">
        <p>
          O painel mede o termo vigente. Enquanto não houver um, os números seriam de um período
          que ninguém combinou.
        </p>
      </Aviso>
    );
  }

  const aderencia =
    painel.ansAvaliados === 0
      ? '—'
      : `${Math.round((painel.ansCumpridos / painel.ansAvaliados) * 100)}%`;
  const consumoPct =
    painel.entitlementAmount !== null
      ? Math.round(((painel.consumo ?? 0) / painel.entitlementAmount) * 100)
      : null;

  return (
    <section className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Período do termo em vigor: {formatarData(painel.periodoDe)} →{' '}
        {painel.periodoAte ? formatarData(painel.periodoAte) : 'indeterminado'}
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador rotulo="Chamados no período" valor={String(painel.chamados)} />
        <Indicador
          rotulo="Tempo apontado"
          valor={formatarMinutos(painel.minutosApontados)}
          nota={
            painel.minutosExcedentes > 0
              ? `${formatarMinutos(painel.minutosExcedentes)} como excedente`
              : undefined
          }
          tom={painel.minutosExcedentes > 0 ? 'alerta' : 'neutro'}
        />
        <Indicador
          rotulo="Franquia"
          valor={
            painel.entitlementAmount === null
              ? 'Ilimitada'
              : `${painel.consumo ?? 0} / ${painel.entitlementAmount}`
          }
          nota={
            painel.entitlementKind === null
              ? 'Sem franquia contratada'
              : painel.entitlementKind === 'HOURS'
                ? 'horas do período'
                : 'chamados do período'
          }
          tom={
            painel.entitlementAmount !== null && (painel.consumo ?? 0) >= painel.entitlementAmount
              ? 'alerta'
              : 'neutro'
          }
        />
        <Indicador
          rotulo="Aderência ao prazo"
          valor={aderencia}
          nota={`${painel.ansCumpridos} de ${painel.ansAvaliados} compromissos encerrados`}
        />
      </div>

      {consumoPct !== null && consumoPct >= 80 && (
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium',
            consumoPct >= 100
              ? 'bg-destructive/10 text-destructive border border-destructive/20'
              : 'bg-amber-500/10 text-amber-600 border border-amber-500/20',
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          {consumoPct >= 100
            ? `Entitlement esgotado — ${consumoPct}% consumido`
            : `Entitlement em ${consumoPct}% — próximo do limite`}
        </div>
      )}

      {/* A distribuição por cobertura é o que separa "muito chamado" de "muito
          chamado fora do combinado", e as duas exigem conversas diferentes. */}
      <div className="flex flex-wrap gap-2 pt-1">
        {COBERTURAS.filter((c) => (painel.chamadosPorCobertura[c.valor] ?? 0) > 0).map((c) => (
          <Etiqueta
            key={c.valor}
            rotulo={`${c.rotulo}: ${painel.chamadosPorCobertura[c.valor]}`}
            tom={c.tom}
            titulo={c.efeito}
          />
        ))}
      </div>

      {histQuery.data && histQuery.data.points.length > 1 && (
        <SparklineChart points={histQuery.data.points} />
      )}
    </section>
  );
}

function LinhaDeTermo({ termo, contrato }: { termo: ContractTermRow; contrato: ContractDetail }) {
  const situacao = situacaoDeTermo(termo.status);
  const [ativando, setAtivando] = useState(false);

  const colisoes = contrato.terms.filter(
    (outro) =>
      outro.id !== termo.id &&
      outro.status === 'ACTIVE' &&
      new Date(termo.startsAt) < new Date(outro.endsAt ?? '9999-12-31') &&
      new Date(outro.startsAt) < new Date(termo.endsAt ?? '9999-12-31'),
  );

  const { mutacao: renovar, erro: erroDeRenovacao } = useEscritaDeContrato(() =>
    renovarTermo(termo.id, {}),
  );

  const { mutacao: excluir } = useEscritaDeContrato(() => excluirRascunhoDeTermo(termo.id));

  return (
    <div className="rounded-lg border border-border px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Base ZERO: o primeiro termo é o 0, como no sistema de origem.
                Somar 1 na tela faria a conferência com o histórico não fechar. */}
            <span className="text-sm font-medium">Termo {termo.sequence}</span>
            <Etiqueta rotulo={situacao.rotulo} tom={situacao.tom} titulo={situacao.efeito} />
            {termo.emVigor ? <Etiqueta rotulo="Em vigor hoje" tom="sucesso" /> : null}
            {termo.autoRenew ? (
              <Etiqueta
                rotulo="Renovação automática"
                tom="info"
                titulo="O sistema prepara o termo seguinte em rascunho na janela de aviso. Ele nunca ativa sozinho."
              />
            ) : null}
          </div>
          <p className="mt-1 text-sm tabular-nums text-muted-foreground">
            {formatarData(termo.startsAt)} →{' '}
            {termo.endsAt ? formatarData(termo.endsAt) : 'indeterminado'}
            {termo.origin !== 'ORIGINAL' ? ` · ${termo.origin.toLowerCase()}` : ''}
          </p>
        </div>

        <div className="flex gap-2">
          {termo.status === 'DRAFT' ? (
            <>
              <Button size="sm" onClick={() => setAtivando(true)}>
                Ativar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={excluir.isPending}
                onClick={() => {
                  if (confirm(`Excluir rascunho do termo ${termo.sequence}? Esta ação não tem volta.`))
                    excluir.mutate();
                }}
                aria-label="Excluir rascunho"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </>
          ) : null}
          {termo.status === 'ACTIVE' && termo.endsAt !== null ? (
            <Button
              size="sm"
              variant="secondary"
              loading={renovar.isPending}
              onClick={() => renovar.mutate()}
            >
              Renovar
            </Button>
          ) : null}
        </div>
      </div>

      {/* Campos financeiros do termo */}
      {(termo.totalValue || termo.monthlyValue || termo.paymentFrequency || termo.commitmentValue || termo.balanceToPay || termo.commitment1stGrade || termo.commitment2ndGrade) ? (
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          {termo.monthlyValue && <span>Mensal: <strong className="text-foreground">R$ {Number(termo.monthlyValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>}
          {termo.totalValue && <span>Total: <strong className="text-foreground">R$ {Number(termo.totalValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>}
          {termo.paymentFrequency && <span>Freq.: <strong className="text-foreground">{{ MONTHLY: 'Mensal', QUARTERLY: 'Trimestral', SEMIANNUAL: 'Semestral', ANNUAL: 'Anual', ONE_TIME: 'Único' }[termo.paymentFrequency]}</strong></span>}
          {termo.commitmentValue && <span>Empenho: <strong className="text-foreground">R$ {Number(termo.commitmentValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>}
          {termo.balanceToPay && <span>Saldo: <strong className="text-foreground">R$ {Number(termo.balanceToPay).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>}
          {termo.commitment1stGrade && <span>Emp. 1º grau: <strong className="text-foreground">R$ {Number(termo.commitment1stGrade).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>}
          {termo.commitment2ndGrade && <span>Emp. 2º grau: <strong className="text-foreground">R$ {Number(termo.commitment2ndGrade).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>}
        </div>
      ) : null}

      {termo.reviewReason ? (
        <p className="mt-2 rounded bg-warning/8 px-3 py-2 text-xs text-foreground ring-1 ring-inset ring-warning/25">
          {termo.reviewReason}
        </p>
      ) : null}

      {erroDeRenovacao ? (
        <p className="mt-2 text-xs text-destructive">{erroDeRenovacao}</p>
      ) : null}

      {termo.status === 'DRAFT' ? (
        <EditorDeServicos termo={termo} />
      ) : (
        <>
          {termo.services.length > 0 ? (
            <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              {termo.services.map((servico) => (
                <li key={servico.id}>
                  {servico.serviceName}
                  {servico.slaPolicyName ? ` — prazo: ${servico.slaPolicyName}` : ' — sem prazo próprio'}
                  {servico.visibleInPortal ? '' : ' · oculto no portal'}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              Nenhum serviço coberto: chamado deste cliente vai nascer fora de escopo.
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Termo em vigor não se edita — renovar ou aditar cria um termo novo. É o que faz "qual era
            o prazo deste chamado em março?" continuar tendo resposta depois de duas renovações.
          </p>
        </>
      )}

      {/* Escopo do termo */}
      <div className="mt-4 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Escopo de cobertura</p>
        <EditorDeEscopo
          contratoId={contrato.id}
          termoId={termo.id}
          isDraft={termo.status === 'DRAFT'}
        />
      </div>

      <DialogoDeAtivacao
        termo={termo}
        colisoes={colisoes}
        aberto={ativando}
        aoFechar={() => setAtivando(false)}
      />
    </div>
  );
}

const KIND_LABEL: Record<string, string> = {
  ALL: 'Escopo total (conta inteira)',
  BY_SITE: 'Por site',
  BY_CI_TYPE: 'Por tipo de CI',
  EXPLICIT_CI: 'CI específico',
  BY_TAG: 'Por tag',
};

const ORIGEM_LABEL: Record<string, string> = {
  ORIGINAL: 'Original',
  RENEWAL: 'Renovação',
  AMENDMENT: 'Aditivo',
  REINSTATEMENT: 'Reintegração',
};

function EditorDeEscopo({
  contratoId,
  termoId,
  isDraft,
}: {
  contratoId: string;
  termoId: string;
  isDraft: boolean;
}) {
  const qc = useQueryClient();
  const qk = ['contratos', 'escopo', termoId];

  const { data: regras = [], isPending } = useQuery({
    queryKey: qk,
    queryFn: () => listarEscopoDoTermo(contratoId, termoId),
  });

  const [form, setForm] = useState({ kind: 'ALL', ciType: '', tag: '', exclude: false });

  const criarMut = useMutation({
    mutationFn: () =>
      criarRegraDeEscopo(contratoId, termoId, {
        kind: form.kind,
        ciType: form.kind === 'BY_CI_TYPE' ? form.ciType : undefined,
        tag: form.kind === 'BY_TAG' ? form.tag : undefined,
        exclude: form.exclude,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk });
      setForm({ kind: 'ALL', ciType: '', tag: '', exclude: false });
    },
  });

  const excluirMut = useMutation({
    mutationFn: (ruleId: string) => excluirRegraDeEscopo(contratoId, termoId, ruleId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk }),
  });

  return (
    <div className="space-y-3">
      {/* Regras existentes */}
      {isPending ? (
        <p className="text-xs text-muted-foreground">Carregando…</p>
      ) : regras.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-muted-foreground">
          Nenhuma regra — escopo padrão: <strong>conta inteira</strong>. Adicione regras abaixo para restringir.
        </div>
      ) : (
        <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
          {regras.map((r: RegraDeEscopo) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                  r.exclude
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-primary/10 text-primary',
                )}
              >
                {r.exclude ? 'Excluir' : 'Incluir'}
              </span>
              <span className="text-xs font-medium">{KIND_LABEL[r.kind]}</span>
              {r.ciType && <span className="font-mono text-xs text-muted-foreground">{r.ciType}</span>}
              {r.tag && <span className="font-mono text-xs text-muted-foreground">#{r.tag}</span>}
              {r.ciId && <span className="font-mono text-[10px] text-muted-foreground">{r.ciId}</span>}
              {r.siteId && <span className="font-mono text-[10px] text-muted-foreground">{r.siteId}</span>}
              <span className="flex-1" />
              {isDraft && (
                <button
                  onClick={() => excluirMut.mutate(r.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  title="Remover regra"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Formulário de adição — só para termos DRAFT */}
      {isDraft && (
        <div className="rounded-xl border border-border p-4 space-y-3">
          <p className="text-xs font-medium text-muted-foreground">Adicionar regra de escopo</p>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">Tipo</label>
              <select
                value={form.kind}
                onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value, ciType: '', tag: '' }))}
                className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-xs focus-visible:outline-none"
              >
                <option value="ALL">Escopo total</option>
                <option value="BY_SITE">Por site</option>
                <option value="BY_CI_TYPE">Por tipo de CI</option>
                <option value="EXPLICIT_CI">CI específico</option>
                <option value="BY_TAG">Por tag</option>
              </select>
            </div>

            {form.kind === 'BY_CI_TYPE' && (
              <div className="space-y-1">
                <label className="text-[10px] text-muted-foreground">Tipo de CI</label>
                <input
                  value={form.ciType}
                  onChange={(e) => setForm((p) => ({ ...p, ciType: e.target.value }))}
                  placeholder="ex.: Server, Switch"
                  className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-xs w-40 focus-visible:outline-none"
                />
              </div>
            )}

            {form.kind === 'BY_TAG' && (
              <div className="space-y-1">
                <label className="text-[10px] text-muted-foreground">Tag</label>
                <input
                  value={form.tag}
                  onChange={(e) => setForm((p) => ({ ...p, tag: e.target.value }))}
                  placeholder="ex.: producao"
                  className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-xs w-36 focus-visible:outline-none"
                />
              </div>
            )}

            {(form.kind === 'BY_SITE' || form.kind === 'EXPLICIT_CI') && (
              <div className="space-y-1 flex-1 min-w-[160px]">
                <label className="text-[10px] text-muted-foreground">
                  {form.kind === 'BY_SITE' ? 'Site (ID)' : 'CI (ID)'}
                </label>
                <input
                  placeholder="UUID do item"
                  className="h-8 w-full rounded-lg border border-input/80 bg-muted/40 px-2 text-xs font-mono focus-visible:outline-none opacity-50 cursor-not-allowed"
                  disabled
                  title="Seletor de CI/site via CMDB — disponível na Fase 14"
                />
              </div>
            )}

            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">Ação</label>
              <select
                value={form.exclude ? 'exclude' : 'include'}
                onChange={(e) => setForm((p) => ({ ...p, exclude: e.target.value === 'exclude' }))}
                className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-xs focus-visible:outline-none"
              >
                <option value="include">Incluir no escopo</option>
                <option value="exclude">Excluir do escopo</option>
              </select>
            </div>

            <button
              onClick={() => criarMut.mutate()}
              disabled={
                criarMut.isPending ||
                (form.kind === 'BY_CI_TYPE' && !form.ciType.trim()) ||
                (form.kind === 'BY_TAG' && !form.tag.trim())
              }
              className="h-8 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 self-end"
            >
              {criarMut.isPending ? '…' : '+ Regra'}
            </button>
          </div>

          {(form.kind === 'BY_SITE' || form.kind === 'EXPLICIT_CI') && (
            <p className="text-[10px] text-muted-foreground">
              Seletor de CI e site via CMDB disponível na Fase 14 — por enquanto, use tipo de CI ou tag para restringir por grupo.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Editor inline de serviços cobertos — só aparece em termos DRAFT.
 *
 * O termo ativo é imutável; editar serviços faz sentido apenas enquanto o
 * termo ainda é rascunho e antes de ativar.
 */
function EditorDeServicos({ termo }: { termo: ContractTermRow }) {
  const queryClient = useQueryClient();
  const [adicionando, setAdicionando] = useState(false);
  const [serviceId, setServiceId] = useState('');
  const [slaPolicyId, setSlaPolicyId] = useState('');
  const [visivel, setVisivel] = useState(true);
  const [erroAdicao, setErroAdicao] = useState('');

  const servicos = useQuery({ queryKey: chavesCatalogo.lista(), queryFn: () => listarServicos() });
  const politicas = useQuery({
    queryKey: chavesDeAdministracao.politicasSla,
    queryFn: listarPoliticasSla,
  });

  const invalidar = () => void queryClient.invalidateQueries({ queryKey: chavesDeContratos.raiz });

  const adicao = useMutation({
    mutationFn: () =>
      adicionarServicoDeTermo(termo.id, {
        serviceId,
        ...(slaPolicyId ? { slaPolicyId } : {}),
        visibleInPortal: visivel,
      }),
    onSuccess: () => {
      invalidar();
      setAdicionando(false);
      setServiceId('');
      setSlaPolicyId('');
      setVisivel(true);
      setErroAdicao('');
    },
    onError: (err: Error) => setErroAdicao(err.message),
  });

  const remocao = useMutation({
    mutationFn: (svcId: string) => removerServicoDeTermo(termo.id, svcId),
    onSuccess: invalidar,
  });

  const servicosJaAdicionados = new Set(termo.services.map((s) => s.serviceId));
  const opcoes = (servicos.data ?? []).filter((s) => !servicosJaAdicionados.has(s.id));

  return (
    <div className="mt-3 space-y-2">
      {termo.services.length > 0 ? (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="pb-1 pr-3 font-medium">Serviço</th>
              <th className="pb-1 pr-3 font-medium">Política de SLA</th>
              <th className="pb-1 pr-3 font-medium">Portal</th>
              <th className="pb-1" />
            </tr>
          </thead>
          <tbody>
            {termo.services.map((s) => (
              <tr key={s.id} className="border-b border-border/50">
                <td className="py-1.5 pr-3 font-medium">{s.serviceName}</td>
                <td className="py-1.5 pr-3 text-muted-foreground">
                  {s.slaPolicyName ?? <span className="italic opacity-60">sem política</span>}
                </td>
                <td className="py-1.5 pr-3 text-muted-foreground">
                  {s.visibleInPortal ? 'Visível' : 'Oculto'}
                </td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    title="Remover serviço"
                    className="text-muted-foreground hover:text-destructive disabled:opacity-40"
                    disabled={remocao.isPending}
                    onClick={() => remocao.mutate(s.serviceId)}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-muted-foreground">
          Nenhum serviço coberto ainda — chamado nascerá fora de escopo até você adicionar pelo menos um.
        </p>
      )}

      {adicionando ? (
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
          {opcoes.length === 0 && !servicos.isLoading ? (
            <p className="text-xs text-muted-foreground italic">
              {servicos.isError
                ? 'Erro ao carregar o catálogo de serviços.'
                : 'Todos os serviços do catálogo já estão cobertos neste termo.'}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Serviço</label>
                <select
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                  className={`${CLASSE_DE_CAMPO} text-xs`}
                  required
                  disabled={servicos.isLoading}
                >
                  <option value="">{servicos.isLoading ? 'Carregando…' : 'Selecione…'}</option>
                  {opcoes.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Política de SLA</label>
                <select
                  value={slaPolicyId}
                  onChange={(e) => setSlaPolicyId(e.target.value)}
                  className={`${CLASSE_DE_CAMPO} text-xs`}
                >
                  <option value="">Sem política própria</option>
                  {(politicas.data?.items ?? []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Visibilidade no portal</label>
                <select
                  value={visivel ? 'sim' : 'nao'}
                  onChange={(e) => setVisivel(e.target.value === 'sim')}
                  className={`${CLASSE_DE_CAMPO} text-xs`}
                >
                  <option value="sim">Visível</option>
                  <option value="nao">Oculto</option>
                </select>
              </div>
            </div>
          )}
          {erroAdicao ? (
            <p className="text-xs text-destructive">{erroAdicao}</p>
          ) : null}
          <div className="flex gap-2">
            {opcoes.length > 0 || servicos.isLoading ? (
              <Button
                size="sm"
                loading={adicao.isPending}
                disabled={!serviceId || servicos.isLoading}
                onClick={() => adicao.mutate()}
              >
                Adicionar
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setAdicionando(false); setErroAdicao(''); }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdicionando(true)}
          className="flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Adicionar serviço
        </button>
      )}
    </div>
  );
}

function DialogoDeEdicaoDeContrato({
  contrato,
  aberto,
  aoFechar,
}: {
  contrato: ContractDetail;
  aberto: boolean;
  aoFechar: () => void;
}) {
  const qc = useQueryClient();
  const [nome, setNome] = useState(contrato.name);
  const [externalNumber, setExternalNumber] = useState(contrato.externalNumber ?? '');
  const [tipo, setTipo] = useState<ContractType>(contrato.type);
  const [notes, setNotes] = useState(contrato.notes ?? '');
  const [signedBy, setSignedBy] = useState(contrato.signedBy ?? '');
  const [signedOnDate, setSignedOnDate] = useState(
    contrato.signedOnDate ? contrato.signedOnDate.slice(0, 10) : '',
  );
  const [renewalWarnDays, setRenewalWarnDays] = useState(String(contrato.renewalWarnDays));
  const [demandingArea, setDemandingArea] = useState(contrato.demandingArea ?? '');
  const [erro, setErro] = useState('');

  const mutacao = useMutation({
    mutationFn: () =>
      alterarContrato(contrato.id, {
        name: nome.trim(),
        type: tipo,
        externalNumber: externalNumber.trim() || undefined,
        notes: notes.trim() || undefined,
        signedBy: signedBy.trim() || undefined,
        signedOnDate: signedOnDate || undefined,
        renewalWarnDays: renewalWarnDays ? Number(renewalWarnDays) : undefined,
        demandingArea: demandingArea.trim() || undefined,
        version: contrato.version,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chavesDeContratos.raiz });
      aoFechar();
    },
    onError: (e: Error) => setErro(e.message),
  });

  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo="Editar contrato">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutacao.mutate();
        }}
      >
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Nome</label>
          <input
            className={CLASSE_DE_CAMPO}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Número no cliente</label>
          <input
            className={CLASSE_DE_CAMPO}
            value={externalNumber}
            placeholder="TJMT_179_2023"
            onChange={(e) => setExternalNumber(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Tipo</label>
          <select
            className={CLASSE_DE_CAMPO}
            value={tipo}
            onChange={(e) => setTipo(e.target.value as ContractType)}
          >
            {TIPOS_DE_CONTRATO.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Observações</label>
          <textarea
            className={CLASSE_DE_CAMPO}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Informações relevantes sobre o contrato..."
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Assinado por</label>
            <input
              className={CLASSE_DE_CAMPO}
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              placeholder="Nome do signatário"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Data da assinatura</label>
            <input
              type="date"
              className={CLASSE_DE_CAMPO}
              value={signedOnDate}
              onChange={(e) => setSignedOnDate(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Área demandante</label>
          <input
            className={CLASSE_DE_CAMPO}
            value={demandingArea}
            onChange={(e) => setDemandingArea(e.target.value)}
            placeholder="Ex: Secretaria de TI"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Aviso de vencimento (dias)</label>
          <input
            type="number"
            min={1}
            max={365}
            className={CLASSE_DE_CAMPO}
            value={renewalWarnDays}
            onChange={(e) => setRenewalWarnDays(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            O sistema avisa o gestor de conta este número de dias antes do vencimento do termo.
          </p>
        </div>
        {erro && (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {erro}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutacao.isPending} disabled={!nome.trim()}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Ativação de termo — e a resolução da sobreposição.
 *
 * Quando há colisão, a janela **exige** que a pessoa marque qual termo sai de
 * cena. O sistema não escolhe: os pares sobrepostos herdados do Topdesk têm
 * fins diferentes, e não existe regra automática que acerte.
 */
function DialogoDeAtivacao({
  termo,
  colisoes,
  aberto,
  aoFechar,
}: {
  termo: ContractTermRow;
  colisoes: ContractTermRow[];
  aberto: boolean;
  aoFechar: () => void;
}) {
  const [marcados, setMarcados] = useState<string[]>([]);
  const [motivo, setMotivo] = useState('');

  const { mutacao, erro } = useEscritaDeContrato(
    () =>
      ativarTermo(termo.id, {
        ...(marcados.length > 0 ? { supersede: marcados } : {}),
        ...(motivo.trim() ? { reason: motivo.trim() } : {}),
        version: termo.version,
      }),
    () => {
      setMarcados([]);
      setMotivo('');
      aoFechar();
    },
  );

  const faltaDecidir = colisoes.some((c) => !marcados.includes(c.id));

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`Ativar o termo ${termo.sequence}`}
      descricao={
        colisoes.length > 0
          ? 'Este período já é coberto por outro termo em vigor. Diga qual deles este substitui — o sistema não escolhe sozinho.'
          : 'A partir da ativação o termo fica imutável: renovar ou aditar cria um termo novo.'
      }
    >
      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          mutacao.mutate();
        }}
      >
        {colisoes.length > 0 ? (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Termos que este substitui</legend>
            {colisoes.map((c) => (
              <label key={c.id} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-border"
                  checked={marcados.includes(c.id)}
                  onChange={(evento) =>
                    setMarcados((atual) =>
                      evento.target.checked
                        ? [...atual, c.id]
                        : atual.filter((id) => id !== c.id),
                    )
                  }
                />
                <span>
                  Termo {c.sequence} · {formatarData(c.startsAt)} →{' '}
                  {c.endsAt ? formatarData(c.endsAt) : 'indeterminado'}
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}

        <div className="space-y-1.5">
          <label htmlFor="ativacao-motivo" className="text-sm font-medium">
            Motivo da decisão
          </label>
          <textarea
            id="ativacao-motivo"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            rows={2}
            className={CLASSE_DE_CAMPO}
            placeholder="Par sobreposto herdado do Topdesk, resolvido a favor do termo mais recente."
          />
          <p className="text-xs text-muted-foreground">
            Fica na trilha de auditoria junto da decisão. Sem ele, daqui a um ano ninguém sabe por
            que um dos dois termos saiu.
          </p>
        </div>

        {erro ? (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutacao.isPending} disabled={faltaDecidir}>
            Ativar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Criação de termo — vigência e, opcionalmente, a franquia do período. */
function DialogoDeTermo({
  contratoId,
  aberto,
  aoFechar,
}: {
  contratoId: string;
  aberto: boolean;
  aoFechar: () => void;
}) {
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);

  const { mutacao, erro } = useEscritaDeContrato(
    () =>
      criarTermo(contratoId, {
        startsAt: new Date(`${inicio}T00:00:00.000Z`).toISOString(),
        ...(fim ? { endsAt: new Date(`${fim}T00:00:00.000Z`).toISOString() } : {}),
        autoRenew,
      }),
    () => {
      setInicio('');
      setFim('');
      setAutoRenew(false);
      aoFechar();
    },
  );

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Novo termo"
      descricao="O termo nasce em rascunho: nada entra em vigor sem alguém ativar. Os serviços cobertos e o parque se ajustam enquanto ele é rascunho."
    >
      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          mutacao.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="termo-inicio" className="text-sm font-medium">
              Início da vigência
            </label>
            <input
              id="termo-inicio"
              type="date"
              value={inicio}
              onChange={(evento) => setInicio(evento.target.value)}
              className={CLASSE_DE_CAMPO}
              required
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="termo-fim" className="text-sm font-medium">
              Fim da vigência
            </label>
            <input
              id="termo-fim"
              type="date"
              value={fim}
              onChange={(evento) => setFim(evento.target.value)}
              className={CLASSE_DE_CAMPO}
            />
            <p className="text-xs text-muted-foreground">
              Em branco significa prazo indeterminado — e um termo indeterminado não se renova.
            </p>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoRenew}
            onChange={(evento) => setAutoRenew(evento.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border"
          />
          <span>
            Renovação automática
            <span className="mt-0.5 block text-xs text-muted-foreground">
              O sistema prepara o termo seguinte em rascunho quando o vencimento se aproxima. Ele
              nunca ativa sozinho: um contrato que se renova sem ninguém olhar é como uma cobrança
              que ninguém confere.
            </span>
          </span>
        </label>

        {erro ? (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutacao.isPending}>
            Criar rascunho
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Suspender, retomar e rescindir.
 *
 * O motivo é obrigatório nas duas decisões que gravam carimbo — e o banco o
 * exige também. Um contrato encerrado sem motivo é a pergunta que ninguém
 * responde seis meses depois.
 */
function DialogoDeDecisao({
  contrato,
  acao,
  aoFechar,
  termoVigente,
}: {
  contrato: ContractDetail;
  acao: 'suspend' | 'terminate' | null;
  aoFechar: () => void;
  termoVigente: ContractTermRow | null;
}) {
  const [motivo, setMotivo] = useState('');
  const retomando = acao === 'suspend' && contrato.suspendedAt !== null;

  const { mutacao, erro } = useEscritaDeContrato(
    () => {
      const corpo = { reason: motivo.trim(), version: contrato.version };
      if (acao === 'terminate') return rescindirContrato(contrato.id, corpo);
      return retomando ? retomarContrato(contrato.id, corpo) : suspenderContrato(contrato.id, corpo);
    },
    () => {
      setMotivo('');
      aoFechar();
    },
  );

  const titulo = acao === 'terminate' ? 'Rescindir contrato' : retomando ? 'Retomar contrato' : 'Suspender contrato';

  return (
    <Modal
      aberto={acao !== null}
      aoFechar={aoFechar}
      titulo={titulo}
      descricao={
        acao === 'terminate'
          ? 'A rescisão não apaga nada: termos, chamados e histórico de prazos continuam consultáveis. Ela também não se desfaz — o que existe depois dela é um contrato novo.'
          : retomando
            ? 'O contrato volta a governar os chamados novos.'
            : 'Chamado novo passa a nascer fora de vigência, com prazo não contratual. O que já está aberto continua como está.'
      }
    >
      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          mutacao.mutate();
        }}
      >
        {acao === 'terminate' && termoVigente ? (
          <Aviso tom="alerta" titulo="Há um termo em vigor hoje">
            <p>
              Termo {termoVigente.sequence}, até{' '}
              {termoVigente.endsAt ? formatarData(termoVigente.endsAt) : 'prazo indeterminado'}. Ele
              continua no histórico depois da rescisão — o que muda é que ele deixa de governar
              chamado novo.
            </p>
          </Aviso>
        ) : null}

        <div className="space-y-1.5">
          <label htmlFor="decisao-motivo" className="text-sm font-medium">
            Motivo
          </label>
          <textarea
            id="decisao-motivo"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            rows={3}
            className={CLASSE_DE_CAMPO}
            required={!retomando}
          />
        </div>

        {erro ? (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutacao.isPending}>
            {titulo}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Timeline de termos ───────────────────────────────────────────────────────

function TimelineDeTermos({ terms }: { terms: ContractTermRow[] }) {
  const sorted = [...terms].sort((a, b) => a.sequence - b.sequence);
  const toMs = (iso: string) => new Date(iso).getTime();
  const nowMs = Date.now();
  const FUTURE = 180 * 86_400_000; // 6 months as placeholder for open-ended terms

  const starts = sorted.map((t) => toMs(t.startsAt));
  const ends = sorted.map((t) => (t.endsAt ? toMs(t.endsAt) : nowMs + FUTURE));
  const minMs = Math.min(...starts);
  const maxMs = Math.max(...ends, nowMs);
  const rangeMs = maxMs - minMs || 1;

  const pct = (ms: number) => Math.max(0, Math.min(100, ((ms - minMs) / rangeMs) * 100));

  const BAR_H = 22;
  const GAP = 6;
  const PAD_TOP = 18;
  const LEGEND_H = 18;
  const totalH = PAD_TOP + sorted.length * (BAR_H + GAP) + LEGEND_H + 6;
  const nowPct = pct(nowMs);

  const STATUS_FILL: Record<string, string> = {
    ACTIVE: '#2BBFCF',
    DRAFT: '#6B8099',
    SUPERSEDED: '#8B9EB0',
    EXPIRED: '#4A6070',
  };
  const STATUS_OPACITY: Record<string, number> = {
    ACTIVE: 1,
    DRAFT: 0.6,
    SUPERSEDED: 0.45,
    EXPIRED: 0.35,
  };

  return (
    <Card>
      <CardHeader title="Linha do tempo dos termos" />
      <CardBody className="pt-2">
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 700 ${totalH}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', minWidth: 400, display: 'block' }}
            aria-label="Linha do tempo dos termos do contrato"
          >
            {sorted.map((termo, i) => {
              const x1 = pct(toMs(termo.startsAt));
              const x2 = pct(termo.endsAt ? toMs(termo.endsAt) : nowMs + FUTURE);
              const y = PAD_TOP + i * (BAR_H + GAP);
              const fill = STATUS_FILL[termo.status] ?? '#6B8099';
              const opacity = STATUS_OPACITY[termo.status] ?? 0.5;
              const w = Math.max(x2 - x1, 0.8);
              const textColor = termo.status === 'ACTIVE' ? '#fff' : '#CBD5E1';
              const barLabel = `T${termo.sequence}`;

              return (
                <g key={termo.id}>
                  <rect
                    x={`${x1}%`}
                    y={y}
                    width={`${w}%`}
                    height={BAR_H}
                    rx={4}
                    fill={fill}
                    opacity={opacity}
                  />
                  <text
                    x={`${x1 + 0.6}%`}
                    y={y + BAR_H / 2 + 4}
                    fontSize={10}
                    fill={textColor}
                    style={{ userSelect: 'none', fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {barLabel}
                    {termo.emVigor ? ' ✦' : ''}
                  </text>
                </g>
              );
            })}

            {/* Today line */}
            {nowPct >= 0 && nowPct <= 100 && (
              <g>
                <line
                  x1={`${nowPct}%`} y1={PAD_TOP - 6}
                  x2={`${nowPct}%`} y2={PAD_TOP + sorted.length * (BAR_H + GAP)}
                  stroke="#F0A500"
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                />
                <text
                  x={`${nowPct}%`} y={PAD_TOP - 9}
                  textAnchor="middle"
                  fontSize={9}
                  fill="#F0A500"
                  style={{ userSelect: 'none' }}
                >
                  Hoje
                </text>
              </g>
            )}

            {/* Legend row */}
            {(['ACTIVE', 'DRAFT', 'SUPERSEDED', 'EXPIRED'] as const).map((s, idx) => {
              const LABELS: Record<string, string> = { ACTIVE: 'Em vigor', DRAFT: 'Rascunho', SUPERSEDED: 'Substituído', EXPIRED: 'Expirado' };
              return (
                <g key={s} transform={`translate(${idx * 100}, ${PAD_TOP + sorted.length * (BAR_H + GAP) + 6})`}>
                  <rect width={10} height={10} rx={2} fill={STATUS_FILL[s]} opacity={STATUS_OPACITY[s]} />
                  <text x={14} y={9} fontSize={9} fill="#7A9BB5" style={{ userSelect: 'none' }}>
                    {LABELS[s]}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Sparkline (Feature 2) ────────────────────────────────────────────────────

function SparklineChart({
  points,
}: {
  points: { date: string; total: number; cobertos: number }[];
}) {
  const W = 400,
    H = 60,
    PAD = 4;
  const maxTotal = Math.max(...points.map((p) => p.total), 1);
  const xs = points.map((_, i) => PAD + (i / (points.length - 1)) * (W - PAD * 2));
  const y = (v: number) => H - PAD - (v / maxTotal) * (H - PAD * 2);

  const totalPath = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y(points[i]!.total).toFixed(1)}`)
    .join(' ');
  const covPath = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y(points[i]!.cobertos).toFixed(1)}`)
    .join(' ');

  const fmtDate = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

  return (
    <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">Chamados nos últimos 30 dias</p>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="inline-block w-6 h-px bg-primary/60" />
            Total
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-6 h-px bg-green-500" />
            Cobertos
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-14"
          preserveAspectRatio="none"
        >
          <path
            d={totalPath}
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.3"
            strokeWidth="1.5"
            className="text-primary"
          />
          <path d={covPath} fill="none" stroke="#3DD68C" strokeWidth="1.5" />
          {points.map((p, i) => (
            <circle key={i} cx={xs[i]} cy={y(p.total)} r="2.5" className="fill-primary/60" />
          ))}
        </svg>
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{fmtDate(points[0]!.date)}</span>
        <span>{fmtDate(points[points.length - 1]!.date)}</span>
      </div>
    </div>
  );
}

// ── Histórico (Feature 1) ────────────────────────────────────────────────────

type EventoHistorico = {
  data: Date;
  tipo:
    | 'criacao'
    | 'termo_criado'
    | 'termo_ativado'
    | 'termo_expirado'
    | 'aditivo'
    | 'suspensao'
    | 'retomada'
    | 'rescisao';
  descricao: string;
};

const EVENTO_ICON: Record<EventoHistorico['tipo'], string> = {
  criacao: '📄',
  termo_criado: '📝',
  termo_ativado: '✅',
  termo_expirado: '⌛',
  aditivo: '📎',
  suspensao: '⏸',
  retomada: '▶',
  rescisao: '🔴',
};

const EVENTO_COLOR: Record<EventoHistorico['tipo'], string> = {
  criacao: 'text-primary',
  termo_criado: 'text-muted-foreground',
  termo_ativado: 'text-green-600',
  termo_expirado: 'text-amber-600',
  aditivo: 'text-blue-500',
  suspensao: 'text-amber-600',
  retomada: 'text-green-600',
  rescisao: 'text-destructive',
};

function HistoricoView({ contrato }: { contrato: ContractDetail }) {
  const eventos: EventoHistorico[] = [];

  // Criação do contrato
  eventos.push({
    data: new Date(contrato.createdAt),
    tipo: 'criacao',
    descricao: 'Contrato criado',
  });

  // Termos
  for (const t of contrato.terms ?? []) {
    if (t.status === 'ACTIVE' || t.status === 'SUPERSEDED' || t.status === 'EXPIRED') {
      eventos.push({
        data: new Date(t.startsAt),
        tipo: 'termo_ativado',
        descricao: `Termo ${t.sequence} ativado (${ORIGEM_LABEL[t.origin] ?? t.origin}) — vigência ${formatarData(t.startsAt)}${t.endsAt ? ` até ${formatarData(t.endsAt)}` : ' (indeterminada)'}`,
      });
    }
    if (t.status === 'EXPIRED' && t.endsAt) {
      eventos.push({
        data: new Date(t.endsAt),
        tipo: 'termo_expirado',
        descricao: `Termo ${t.sequence} expirado`,
      });
    }
    if (t.status === 'SUPERSEDED' && t.supersededAt) {
      eventos.push({
        data: new Date(t.supersededAt),
        tipo: 'termo_expirado',
        descricao: `Termo ${t.sequence} substituído`,
      });
    }
  }

  // Aditivos
  for (const a of contrato.amendments ?? []) {
    eventos.push({
      data: new Date(a.signedAt),
      tipo: 'aditivo',
      descricao: `Aditivo ${a.number} assinado${a.summary ? ` — ${a.summary}` : ''}`,
    });
  }

  // Suspensão / rescisão
  if (contrato.suspendedAt) {
    eventos.push({
      data: new Date(contrato.suspendedAt),
      tipo: 'suspensao',
      descricao: `Suspenso${contrato.suspensionReason ? `: ${contrato.suspensionReason}` : ''}`,
    });
  }
  if (contrato.terminatedAt) {
    eventos.push({
      data: new Date(contrato.terminatedAt),
      tipo: 'rescisao',
      descricao: `Rescindido${contrato.terminationReason ? `: ${contrato.terminationReason}` : ''}`,
    });
  }

  eventos.sort((a, b) => b.data.getTime() - a.data.getTime());

  if (eventos.length === 0) {
    return <p className="text-xs text-muted-foreground">Nenhum evento registrado.</p>;
  }

  return (
    <div className="relative space-y-0 pl-5">
      <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
      {eventos.map((e, i) => (
        <div key={i} className="relative pb-4">
          <div className="absolute -left-3 top-0.5 text-sm">{EVENTO_ICON[e.tipo]}</div>
          <div className={cn('text-xs font-medium', EVENTO_COLOR[e.tipo])}>{e.descricao}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {e.data.toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Rótulos ────────────────────────────────────────────────────────────────

const ROTULO_ROLE: Record<StakeholderRole, string> = {
  PREPOSTO: 'Preposto',
  FISCAL: 'Fiscal',
  GESTOR: 'Gestor',
  TECNICO: 'Técnico',
  DEMANDANTE: 'Demandante',
  GESTOR_INTERNO: 'Gestor interno',
  PRESTADOR: 'Prestador',
};
const ROTULO_KIND: Record<ContractAttachmentKind, string> = {
  CONTRATO: 'Contrato',
  EDITAL: 'Edital',
  PROPOSTA: 'Proposta',
  GARANTIA: 'Garantia',
  ADITIVO: 'Aditivo',
  OUTRO: 'Outro',
};

// ── Seção de Partes ─────────────────────────────────────────────────────────

function SecaoDePartes({ contrato }: { contrato: ContractDetail }) {
  const qc = useQueryClient();
  const [adicionando, setAdicionando] = useState(false);

  const invalidar = () => void qc.invalidateQueries({ queryKey: chavesDeContratos.raiz });

  const remover = useMutation({
    mutationFn: (id: string) => removerParte(contrato.id, id),
    onSuccess: invalidar,
  });

  const aptum = contrato.stakeholders.filter((s) => s.side === 'APTUM');
  const cliente = contrato.stakeholders.filter((s) => s.side === 'CLIENTE');

  return (
    <Card>
      <CardHeader
        title="Partes envolvidas"
        description="Prepostos da Aptum e fiscais do cliente."
        action={
          <Button
            size="sm"
            variant="ghost"
            icon={<Plus className="h-4 w-4" aria-hidden />}
            onClick={() => setAdicionando(true)}
          >
            Adicionar
          </Button>
        }
      />
      <CardBody className="space-y-4">
        {contrato.stakeholders.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="Nenhuma parte cadastrada"
            description="Adicione os prepostos da Aptum e os fiscais do cliente."
          />
        ) : (
          <>
            {aptum.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Aptum
                </p>
                <div className="space-y-2">
                  {aptum.map((s) => (
                    <ParteRow key={s.id} parte={s} onRemover={() => remover.mutate(s.id)} />
                  ))}
                </div>
              </div>
            )}
            {cliente.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Cliente
                </p>
                <div className="space-y-2">
                  {cliente.map((s) => (
                    <ParteRow key={s.id} parte={s} onRemover={() => remover.mutate(s.id)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>

      {adicionando && (
        <FormularioDeParte
          contratoId={contrato.id}
          aoFechar={() => setAdicionando(false)}
          aoSalvar={invalidar}
        />
      )}
    </Card>
  );
}

function ParteRow({ parte, onRemover }: { parte: ContractStakeholderRow; onRemover: () => void }) {
  const nome = parte.name ?? '—';
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{nome}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
            {ROTULO_ROLE[parte.role]}
          </span>
          {parte.isPrimary && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              Titular
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          {parte.email && <span>{parte.email}</span>}
          {parte.phone && <span>{parte.phone}</span>}
          {parte.notes && <span className="italic">{parte.notes}</span>}
        </div>
      </div>
      <button
        onClick={onRemover}
        aria-label="Remover parte"
        className="shrink-0 text-muted-foreground hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

function FormularioDeParte({
  contratoId,
  aoFechar,
  aoSalvar,
}: {
  contratoId: string;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [side, setSide] = useState<StakeholderSide>('APTUM');
  const [role, setRole] = useState<StakeholderRole>('PREPOSTO');
  const [isPrimary, setIsPrimary] = useState(false);
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const operadores = useQuery({
    queryKey: chavesDeAdministracao.operadores,
    queryFn: listarOperadores,
  });

  const adicionar = useMutation({
    mutationFn: (corpo: NovaParte) => adicionarParte(contratoId, corpo),
    onSuccess: () => { aoSalvar(); aoFechar(); },
  });

  function submeter(e: React.FormEvent) {
    e.preventDefault();
    const corpo: NovaParte = {
      side,
      role,
      isPrimary,
      userId: userId || undefined,
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    adicionar.mutate(corpo);
  }

  return (
    <Modal titulo="Adicionar parte" aberto aoFechar={aoFechar}>
      <form onSubmit={submeter} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium">Lado</label>
            <select
              value={side}
              onChange={(e) => setSide(e.target.value as StakeholderSide)}
              className={CLASSE_DE_CAMPO}
            >
              <option value="APTUM">Aptum</option>
              <option value="CLIENTE">Cliente</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium">Papel</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as StakeholderRole)}
              className={CLASSE_DE_CAMPO}
            >
              <option value="PREPOSTO">Preposto</option>
              <option value="FISCAL">Fiscal</option>
              <option value="GESTOR">Gestor</option>
              <option value="TECNICO">Técnico</option>
              <option value="DEMANDANTE">Demandante</option>
            </select>
          </div>
        </div>

        {side === 'APTUM' && (
          <div>
            <label className="text-xs font-medium">Operador (opcional)</label>
            <select
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                const op = operadores.data?.items.find((u) => u.id === e.target.value);
                if (op && !name) setName(op.name);
              }}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">— Digitar manualmente —</option>
              {(operadores.data?.items ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="text-xs font-medium">Nome</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome completo"
            className={CLASSE_DE_CAMPO}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={CLASSE_DE_CAMPO}
            />
          </div>
          <div>
            <label className="text-xs font-medium">Telefone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+55 65 9 9999-9999"
              className={CLASSE_DE_CAMPO}
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium">Observações</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='Ex: "Substitui o titular nas ausências"'
            className={CLASSE_DE_CAMPO}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="rounded"
          />
          {role === 'FISCAL' ? 'Fiscal titular (não substituto)' : 'Responsável principal'}
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={adicionar.isPending}>
            Adicionar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Seção de Documentos ────────────────────────────────────────────────────

function SecaoDeDocumentos({ contrato }: { contrato: ContractDetail }) {
  const qc = useQueryClient();
  const [adicionando, setAdicionando] = useState(false);

  const invalidar = () => void qc.invalidateQueries({ queryKey: chavesDeContratos.raiz });

  const remover = useMutation({
    mutationFn: (id: string) => removerAnexo(contrato.id, id),
    onSuccess: invalidar,
  });

  const todosAnexos = [
    ...(contrato.documentUrl
      ? [{ id: '__legacy__', kind: 'CONTRATO' as ContractAttachmentKind, label: 'Documento', url: contrato.documentUrl, uploadedByUserId: null, uploadedByName: null, createdAt: contrato.createdAt }]
      : []),
    ...contrato.attachments,
  ];

  return (
    <Card>
      <CardHeader
        title="Documentos"
        description="Contrato assinado, edital, proposta, garantia e outros anexos."
        action={
          <Button
            size="sm"
            variant="ghost"
            icon={<Plus className="h-4 w-4" aria-hidden />}
            onClick={() => setAdicionando(true)}
          >
            Anexar
          </Button>
        }
      />
      <CardBody className="space-y-2">
        {todosAnexos.length === 0 ? (
          <EmptyState
            icon={<Paperclip className="h-6 w-6" />}
            title="Nenhum documento anexado"
            description="Adicione o contrato assinado, edital, proposta ou outros documentos."
          />
        ) : (
          todosAnexos.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{a.label}</p>
                  <p className="text-xs text-muted-foreground">{ROTULO_KIND[a.kind]}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Abrir documento"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                </a>
                {a.id !== '__legacy__' && (
                  <button
                    onClick={() => {
                      if (confirm(`Remover "${a.label}"?`)) remover.mutate(a.id);
                    }}
                    aria-label="Remover documento"
                    className="text-muted-foreground hover:text-destructive ml-1"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </CardBody>

      {adicionando && (
        <FormularioDeAnexo
          contratoId={contrato.id}
          aoFechar={() => setAdicionando(false)}
          aoSalvar={invalidar}
        />
      )}
    </Card>
  );
}

function FormularioDeAnexo({
  contratoId,
  aoFechar,
  aoSalvar,
}: {
  contratoId: string;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [kind, setKind] = useState<ContractAttachmentKind>('CONTRATO');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');

  const adicionar = useMutation({
    mutationFn: (corpo: NovoAnexo) => adicionarAnexo(contratoId, corpo),
    onSuccess: () => { aoSalvar(); aoFechar(); },
  });

  function submeter(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim() || !url.trim()) return;
    adicionar.mutate({ kind, label: label.trim(), url: url.trim() });
  }

  return (
    <Modal titulo="Anexar documento" aberto aoFechar={aoFechar}>
      <form onSubmit={submeter} className="space-y-4">
        <div>
          <label className="text-xs font-medium">Tipo</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as ContractAttachmentKind)}
            className={CLASSE_DE_CAMPO}
          >
            {(Object.entries(ROTULO_KIND) as [ContractAttachmentKind, string][]).map(([v, r]) => (
              <option key={v} value={v}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">Descrição</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            placeholder='Ex: "Contrato assinado v2", "Edital 019/2020"'
            className={CLASSE_DE_CAMPO}
          />
        </div>
        <div>
          <label className="text-xs font-medium">URL do documento</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            placeholder="https://..."
            className={CLASSE_DE_CAMPO}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Cole o link do documento armazenado em nuvem (SharePoint, Drive, etc.).
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={adicionar.isPending}>
            Anexar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
